/**
 * IMPORTS
 */
import { CheckCheck } from "lucide-react";

import { NotificacaoLinha } from "@components/notificacoes/notificacao-linha";
import { NotificacoesSino } from "@components/notificacoes/notificacoes-sino";
import { useAbrirNotificacao } from "@components/notificacoes/use-abrir-notificacao";

import {
  useListaNotificacoes,
  useMarcarNotificacoes,
} from "@/hooks/conversa-cliente/use-notificacoes";

import { getApiErrorMessage } from "@/utils/normalize-api-error/normalize-api-error";
import { notifyError } from "@/utils/notify-messages";

const POR_PAGINA = 20;

function NotificacoesPage() {
  const lista = useListaNotificacoes(POR_PAGINA);
  const { marcarTodas } = useMarcarNotificacoes();
  const abrirNotificacao = useAbrirNotificacao();

  const notificacoes = lista.data?.pages.flatMap((pagina) => pagina.data) ?? [];
  const naoLidas = lista.data?.pages[0]?.nao_lidas ?? 0;

  return (
    <div className="w-full">
      <div className="flex items-center gap-3">
        <h2 className="p-4 text-2xl font-medium text-black">Notificações</h2>
        <div className="ml-auto mr-4">
          <NotificacoesSino />
        </div>
      </div>

      <section className="mx-4 rounded-xl border border-gray-200 bg-white shadow-sm">
        <header className="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3">
          <p className="flex-1 text-sm text-gray-600">
            {naoLidas === 0 ? "Tudo lido." : `${naoLidas} não lida${naoLidas > 1 ? "s" : ""}.`}
          </p>
          <button
            type="button"
            disabled={naoLidas === 0 || marcarTodas.isPending}
            onClick={() =>
              marcarTodas.mutate(undefined, {
                onError: (erro) => notifyError(getApiErrorMessage(erro)),
              })
            }
            className="flex items-center gap-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >
            <CheckCheck className="size-4" />
            Marcar todas como lidas
          </button>
        </header>

        <div>
          {lista.isLoading ? (
            <div className="space-y-2 p-2">
              {[0, 1, 2, 3].map((indice) => (
                <div key={indice} className="h-14 animate-pulse rounded-md bg-gray-100" />
              ))}
            </div>
          ) : lista.isError ? (
            <div className="p-8 text-center">
              <p className="text-sm text-gray-500">Não foi possível carregar as notificações.</p>
              <button
                type="button"
                onClick={() => lista.refetch()}
                className="mt-4 rounded-md border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
              >
                Tentar novamente
              </button>
            </div>
          ) : notificacoes.length === 0 ? (
            <p className="p-8 text-center text-sm text-gray-500">Você não tem notificações.</p>
          ) : (
            // divisória entre as notificações (a última fica sem borda)
            <div className="divide-y divide-gray-200">
              {notificacoes.map((notificacao) => (
                <NotificacaoLinha
                  key={notificacao.id}
                  notificacao={notificacao}
                  onAbrir={abrirNotificacao}
                />
              ))}
            </div>
          )}
        </div>

        {lista.hasNextPage && (
          <footer className="border-t border-gray-200 p-2 text-center">
            <button
              type="button"
              onClick={() => lista.fetchNextPage()}
              disabled={lista.isFetchingNextPage}
              className="rounded-md px-4 py-2 text-sm font-medium text-orange-600 hover:bg-orange-50 disabled:opacity-50"
            >
              {lista.isFetchingNextPage ? "Carregando..." : "Carregar mais"}
            </button>
          </footer>
        )}
      </section>
    </div>
  );
}

/**
 * EXPORTS
 */
export { NotificacoesPage };
